/** Shared filter options for the medical partner admin list (UI English). */

import { API_BASE_URL } from "../../../utils/Utils";

export const MEDICAL_PARTNER_STATUS_FILTER_OPTIONS = [
  { value: "Active", label: "Live (verified)" },
  { value: "Pending", label: "Pending review" },
  { value: "Suspend", label: "Suspended / inactive" },
];

/** List filter values match API `tcApprovalStatus` (canonical inactive value: `off`). */
export const MEDICAL_PARTNER_TC_FILTER_OPTIONS = [
  { value: "off", label: "TC off" },
  { value: "pending", label: "TC pending" },
  { value: "approved", label: "TC on" },
  { value: "rejected", label: "TC rejected" },
];

/** Partner dossier — platform TC: three product states. `off` is the API inactive value (clears `rejected` when applied). */
export const PARTNER_TC_PLATFORM_SELECT_OPTIONS = [
  { value: "approved", label: "On" },
  { value: "pending", label: "Pending" },
  { value: "off", label: "Off" },
];

/** Partner dossier Overview — MT program matches API boolean (`offersMedicalTourism`). */
export const PARTNER_TM_PROGRAM_SELECT_OPTIONS = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

export function normalizeTcApprovalStatus(partnerOrStatus) {
  const raw =
    typeof partnerOrStatus === "string"
      ? partnerOrStatus
      : partnerOrStatus?.tcApprovalStatus ?? "off";
  const s = String(raw ?? "off").trim().toLowerCase();
  if (s === "approved") return "approved";
  if (s === "pending") return "pending";
  if (s === "rejected") return "rejected";
  if (s === "not_requested") return "off";
  if (s === "off") return "off";
  return "off";
}

/** UI bucket for badges and the primary 3-way control. */
export function tcApprovalUiBucket(partnerOrStatus) {
  const s = normalizeTcApprovalStatus(partnerOrStatus);
  if (s === "approved") return "on";
  if (s === "pending") return "pending";
  return "off";
}

/** Value for `<select>` options (`PARTNER_TC_PLATFORM_SELECT_OPTIONS`). */
export function tcApprovalSelectValueFromPartner(partner) {
  const s = normalizeTcApprovalStatus(partner);
  if (s === "approved") return "approved";
  if (s === "pending") return "pending";
  return "off";
}

/** Maps primary select value to API `tcApprovalStatus`. */
export function tcApprovalApiStatusFromSelectValue(selectValue) {
  const v = String(selectValue || "").toLowerCase();
  if (v === "approved") return "approved";
  if (v === "pending") return "pending";
  return "off";
}

export function tcApprovalStatusBadgeLabel(partnerOrStatus) {
  const b = tcApprovalUiBucket(partnerOrStatus);
  if (b === "on") return "On";
  if (b === "pending") return "Pending";
  return "Off";
}

/** Grid select — same enum as API (draft, pending, active, inactive). Labels: Live = verified / publicly listable when APIs enforce it. */
export const PARTNER_WORKFLOW_OPTIONS = [
  { value: "active", label: "Live" },
  { value: "pending", label: "Pending" },
  { value: "draft", label: "Draft" },
  { value: "inactive", label: "Inactive" },
];

export function buildCountryFilterOptionsFromPartners(partners) {
  if (!Array.isArray(partners)) return [];
  return [
    ...new Set(partners.map((item) => item?.contact_person_country).filter(Boolean)),
  ].map((country) => ({ value: country, label: country }));
}

/** Absolute URL for list avatar; returns null when no logo field. */
export function resolvePartnerOrganizationLogoUrl(partner) {
  const raw = partner?.organization_logo || partner?.default_logo;
  if (!raw || typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith("/") ? t : `/${t}`;
  const base = String(API_BASE_URL || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

/** Medical tourism packages / travel-assisted care. Undefined or missing field = true (legacy partners). */
export function partnerOffersMedicalTourism(partner) {
  return partner?.offersMedicalTourism !== false;
}
